<template>
	<!-- LSP-21829, Oliver's 2026-10-19 review: the section only appears once a customer has submitted a rating.
	     No request, a request still waiting on a rating, loading and a failed load all show nothing. -->
	<div v-if="ratedItems.length > 0" v-test="'entity-customer-reviews'" class="d-flex flex-column ga-2 mt-4">
		<div class="media-header d-flex align-center">
			<div v-test="'entity-customer-reviews-header'" class="heading">Customer Review</div>
		</div>
		<div class="d-flex flex-column ga-3">
			<div
				v-for="(item, index) in ratedItems"
				:key="index"
				v-test="'entity-customer-review-rated'"
				class="d-flex flex-column ga-1 customer-review-item">
				<div class="d-flex align-center ga-2">
					<ReviewStars :rating="item.rating" />
					<span v-if="item.ratingLabel" class="text-body-2 font-weight-medium">{{ item.ratingLabel }}</span>
				</div>
				<p v-if="item.reviewText" v-test="'entity-customer-review-text'" class="text-body-2 customer-review-text mb-0">
					{{ item.reviewText }}
				</p>
				<div v-test="'entity-customer-review-byline'" class="text-caption text-medium-emphasis">
					{{ item.customer.primary }} · Rated {{ formatCustomerReviewDate(item.submittedAt) }}
				</div>
				<div v-if="item.sentToGoogleAt" v-test="'entity-customer-review-sent-to-google'" class="text-caption text-medium-emphasis">
					Sent to Google
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, ref, watch } from "vue";
	import api from "@/api";
	import ReviewStars from "@/components/common/ReviewStars.vue";
	import { formatCustomerReviewDate } from "@/helpers/customerReviews";
	import { logWarn } from "@/helpers/logError";
	import type { CustomerReviewCardItem } from "@/types/CustomerReview";

	/**
	 * LSP-21829 (LSP-22235) — the Customer Review section of a session's or project's About tab: every rating
	 * the customer left. Requests still waiting on a rating (pre-feature requests included) come back from the API
	 * as "sent" items and are not shown; the list's Google icon already says a request is out.
	 */
	const props = defineProps<{ entityType: "session" | "project"; entityId: string }>();

	const items = ref<CustomerReviewCardItem[]>([]);
	const ratedItems = computed(() => items.value.filter((item) => item.state === "rated"));

	let latestRequestId = 0;
	let isUnmounted = false;

	async function load(): Promise<void> {
		const requestId = ++latestRequestId;
		const { entityType, entityId } = props;
		if (!entityId) {
			items.value = [];
			return;
		}
		// Clear the previous entity's ratings so they never show under the next one while it loads.
		items.value = [];
		try {
			const response =
				entityType === "project" ? await api.getProjectCustomerReviews(entityId) : await api.getSessionCustomerReviews(entityId);
			if (isUnmounted || requestId !== latestRequestId) return;
			items.value = Array.isArray(response?.items) ? response.items : [];
		} catch (error) {
			// Logged before the stale/unmounted check: a load that fails during a tab switch still failed.
			const superseded = isUnmounted || requestId !== latestRequestId;
			logWarn("EntityCustomerReviews: loading the customer reviews failed", {
				entityType,
				entityId,
				superseded,
				error: error instanceof Error ? error.message : String(error)
			});
			if (superseded) return;
			// The section stays hidden: the rest of the About tab still works, and the warning above is the trail.
			items.value = [];
		}
	}

	watch(() => [props.entityType, props.entityId], load, { immediate: true });

	onBeforeUnmount(() => {
		isUnmounted = true;
	});
</script>

<style scoped>
	.customer-review-text {
		white-space: pre-wrap;
		word-break: break-word;
	}
</style>
